import React, { useEffect, useState, useCallback } from 'react'
import styled from 'styled-components'

import ModalButton from 'components/Modal/Button'
import TextInput from 'components/TextInput'
import Checkbox from 'components/Checkbox'

import Brand from 'helpers/brand'
import { isEmailValid } from 'helpers/validations'

import { ButtonContainer } from 'styles/shared'
import { color } from 'styles/colors'

import useDealer from 'hooks/useDealer'

type Props = {
  customerName: string,
  customerEmail: string,
  isEmailSent: boolean,
  sendEmail: (notes: string, replyToEmail: string, makeReplyToMailDefault: boolean) => void
  resetAndCloseModal
}
const EmailSystemReport = ({
  customerName,
  customerEmail,
  sendEmail,
  resetAndCloseModal,
  isEmailSent
}: Props) => {
  const { dealer: { name: dealerName, email: dealerEmail, defaultReplyMailAddress }, isDealerLoaded } = useDealer()

  const [emailData, setEmailData] = useState({
    notes: '',
    customerName: '',
    customerEmail: '',
    replyToEmail: ''
  })

  const [makeReplyToMailDefault, setMakeReplyToMailDefault] = useState(false)
  const [showValidationError, setShowValidationError] = useState(false)

  useEffect(() => {
    if (isDealerLoaded) {
      setEmailData(prev => ({
        ...prev,
        customerName,
        customerEmail,
        replyToEmail: defaultReplyMailAddress || Brand.noReplySender(),
        notes: `
          Hello ${customerName},
          <br /><br />
          Your HVAC system report, generated for your system from ${Brand.productName()} is attached.
          If you have any concerns, contact ${dealerName}.`
      }))
    }
  }, [dealerEmail, dealerName, customerName, customerEmail, defaultReplyMailAddress, isDealerLoaded])

  const submit = useCallback(() => {
    if (isEmailValid(emailData.replyToEmail)) {
      setShowValidationError(false)

      sendEmail(emailData.notes, emailData.replyToEmail, makeReplyToMailDefault)
    } else {
      setShowValidationError(true)
    }
  }, [emailData, makeReplyToMailDefault, sendEmail])

  return (
    <>
      <S.Form>
        <S.FieldWrapper>
          <S.Field> <span>Dealer:</span> {dealerName}</S.Field>
          <S.ReplyToWrapper>
            <S.ReplyToInput
              type="email"
              value={emailData.replyToEmail}
              onChange={(e) => setEmailData(prev => ({ ...prev, replyToEmail: e.target.value }))}
              placeholder="Reply-to email address"
              label="Reply to"
            />
            <S.Checkbox
              checked={makeReplyToMailDefault}
              onChange={() => setMakeReplyToMailDefault(!makeReplyToMailDefault)}
              label="Make this reply-to address the default"
            />
          </S.ReplyToWrapper>
        </S.FieldWrapper>

        <S.FieldWrapper>
          <S.Field> <span>Customer Name:</span> {customerName}</S.Field>
          <S.Field> <span>Customer Email:</span> {customerEmail}</S.Field>
        </S.FieldWrapper>

        <S.FieldWrapper>
          <S.NoteArea dangerouslySetInnerHTML={{ __html: emailData.notes }} />
          <S.MessageSubtext>The text in this email cannot be changed.</S.MessageSubtext>
        </S.FieldWrapper>
      </S.Form>

      <S.ButtonContainer noBorder>
        { showValidationError &&
          <S.ValidationErrorMessage style={{ color: 'red' }}>Please enter a valid reply-to email address.</S.ValidationErrorMessage>
        }
        <ModalButton onPress={resetAndCloseModal} decoration="secondary" text="Cancel" disabled={isEmailSent} />
        <ModalButton onPress={() => submit()} decoration="positive" text="Send Email" showSpinner={isEmailSent} />
      </S.ButtonContainer>
    </>
  )
}

const S = {
  Form: styled.div`
    display: flex;
    gap: 25px;
    flex-direction: column;
    font-size: 14px;
    color: ${color('primaryText')};
  `,
  FieldWrapper: styled.div`
    margin-bottom: 4px;
  `,
  NoteArea: styled.div`
    background-color: ${color('noteBackground')};
    padding: 8px;
    border-radius: 10px;
  `,
  Field: styled.div`
    font-weight: bold;

    span {
      font-weight: normal;
    }
  `,
  ReplyToWrapper: styled.div`
    margin-top: 8px;
    margin-bottom: 0;
  `,
  ReplyToInput: styled(TextInput)`
    display: flex;
    margin-bottom: 0;
    align-items: center;

    label {
      width: 68px;
    }
  `,
  Checkbox: styled(Checkbox)`
    margin: 8px 0 0 70px;
  `,
  MessageSubtext: styled.span`
    display: block;
    margin-top: 5px;
    color: ${color('secondaryText')};
    font-style: italic;
  `,
  ButtonContainer: styled(ButtonContainer)`
    margin-top: 0;
    align-items: center;
  `,
  ValidationErrorMessage: styled.div`
    margin-right: 20px;
    color: ${color('errorMessage')};
  `
}
export default EmailSystemReport
